import { z } from 'zod';
import type { ViewerFilters } from '@/api/conversation-viewer';
import { resolveRange } from '@/features/analytics/range';

/**
 * Parámetros de URL del visor de conversaciones: tenant, periodo, filtros y búsqueda viven en la URL (compartible).
 * `includeTests` solo se escribe cuando se DESMARCA (`includeTests=false`): por omisión Martech incluye las pruebas, a diferencia del portal.
 */
export const conversationsSearchSchema = z.object({
  tenant: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  tz: z.string().optional(),
  agent: z.string().optional(),
  channel: z.enum(['widget', 'whatsapp', 'instagram', 'simulator', 'api']).optional().catch(undefined),
  status: z.enum(['open', 'closed', 'handoff']).optional().catch(undefined),
  outcome: z.enum(['lead', 'appointment', 'handoff', 'none']).optional().catch(undefined),
  /** Etiqueta del resumen con IA (clave de la taxonomía del tenant). */
  topic: z
    .string()
    .regex(/^[a-z][a-z0-9_]{0,39}$/)
    .optional()
    .catch(undefined),
  includeTests: z.boolean().optional().catch(undefined),
  /** El router convierte a número un `q=1234` escrito a mano: se acepta y se vuelve texto. */
  q: z
    .union([z.string(), z.number()])
    .transform((v) => String(v))
    .pipe(z.string().max(100))
    .optional()
    .catch(undefined),
});
export type ConversationsSearch = z.infer<typeof conversationsSearchSchema>;

export const conversationDetailSearchSchema = z.object({
  tenant: z.string().optional(),
  /** «Ver técnico» (activado por omisión): solo se escribe en la URL cuando se DESACTIVA. */
  technical: z.boolean().optional().catch(undefined),
});
export type ConversationDetailSearch = z.infer<typeof conversationDetailSearchSchema>;

/** Filtros efectivos para la API (rango por omisión de 30 días en la zona del tenant). */
export function toViewerFilters(search: ConversationsSearch, tz: string): ViewerFilters {
  const r = resolveRange(search, tz);
  const q = search.q?.trim();
  return {
    ...r,
    tz,
    agentId: search.agent,
    channel: search.channel,
    status: search.status,
    outcome: search.outcome,
    topic: search.topic,
    includeTests: search.includeTests === false ? false : undefined,
    q: q && q.length >= 3 ? q : undefined,
  };
}
